import { useMemo } from 'react';
import { useTenant } from '../tenant/useTenant';

interface MLSSubscriptionStatus {
  /** True if all submitted MLS entries are approved */
  allMLSSubscribed: boolean;
  /** True if there are no MLS entries submitted OR all are subscribed */
  canScheduleFlows: boolean;
  /** Number of MLS entries submitted */
  totalMLSEntries: number;
  /** Number of MLS entries that are subscribed/approved */
  subscribedCount: number;
  /** List of MLS that are pending approval */
  pendingMLS: string[];
  /** Loading state */
  isLoading: boolean;
}

/**
 * Hook to check if all MLS entries are subscribed/approved.
 * Used to determine if flows can be scheduled.
 *
 * NOTE: Only checks MLS subscription status if onboardingData.idx.mls_entries exists.
 * Organizations without mls_entries can schedule flows freely.
 */
export const useMLSSubscriptionStatus = (organization: any): MLSSubscriptionStatus => {
  const { data: tenant, isLoading } = useTenant(organization?.id);

  return useMemo(() => {
    // Get MLS entries from onboarding data (what user submitted)
    const mlsEntries = organization?.onboardingData?.idx?.mls_entries;

    // If mls_entries doesn't exist, don't block flow scheduling
    // This allows legacy orgs or orgs that didn't submit MLS info to work normally
    if (!mlsEntries || mlsEntries.length === 0) {
      return {
        allMLSSubscribed: true,
        canScheduleFlows: true,
        totalMLSEntries: 0,
        subscribedCount: 0,
        pendingMLS: [],
        isLoading,
      };
    }

    // Get subscribed MLS from tenant collection (approved MLS - boardCode array)
    const subscribedMLS = tenant?.subscribedMLS || [];

    // Check which MLS are pending
    const pendingMLS = mlsEntries
      .filter((entry: any) => !subscribedMLS.includes(entry.mls_name))
      .map((entry: any) => entry.mls_name || 'Unknown MLS');

    const subscribedCount = mlsEntries.length - pendingMLS.length;
    const allMLSSubscribed = pendingMLS.length === 0;

    return {
      allMLSSubscribed,
      canScheduleFlows: allMLSSubscribed,
      totalMLSEntries: mlsEntries.length,
      subscribedCount,
      pendingMLS,
      isLoading,
    };
  }, [organization?.onboardingData?.idx?.mls_entries, tenant?.subscribedMLS, isLoading]);
};
